/**
 * The one shell every non-prose row in the thread is drawn in (RFC 0300 §4g).
 *
 * A thread is prose and things. The things were three visual systems: an
 * artifact card (`ThreadCard`), a group of them (`ThreadCardGroup`), and the
 * older bordered tool-call block (`ToolCallGroup`), which is what a run of
 * greps or a Bash call still drew as. Three borders, three header layouts,
 * three disclosures, and a thread that read as two products stitched together.
 *
 * So the shell is one component and the fills are data. Header: a verb, a
 * label that is a link when there is somewhere to go, an optional
 * count, an optional trailing note (a state, a stat), and a chevron when there
 * is something under it. One optional line of detail. A body, when the fill
 * has one: rows, a diff, a call's arguments.
 *
 * It knows nothing about documents, sessions, comments or tools. That is the
 * point: a fill that needs the shell to know its kind is a fill that will draw
 * itself differently from the others.
 *
 * NO GLYPH. The first cut had a lucide icon per kind, and it read as
 * decoration: the verb already says what happened. A target with an identity
 * of its own carries that instead (`mark`): a session draws its mark (RFC 0311).
 * Pending is a pulsing dot before the verb, which is a state, not a picture.
 */

import { ChevronDown, ChevronRight } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { StatusDot } from '../../shell/window/StatusDot'

export function CardShell({
  verb,
  label,
  onOpen,
  times = 1,
  detail,
  trailing,
  pending = false,
  body,
  open: openProp,
  defaultOpen = false,
  onToggle,
  always = false,
  mark,
}: {
  /** What happened, past tense, the way a person would say it. */
  verb: string
  /** What it happened to. */
  label: string
  /** The target's own icon, drawn before its label: a session's mark
   *  (RFC 0311). A real identity, never a stand-in glyph for a kind. */
  mark?: ReactNode
  /** Somewhere to go. A button ONLY when there is one: styling a span as a link
   *  is how a card promises a pane it cannot open. */
  onOpen?: () => void
  /** How many consecutive times this same thing happened. Shown as ×N. */
  times?: number
  /** One line under the header, truncated. Context, never content. */
  detail?: string
  /** A state word or a stat, at the right of the header. */
  trailing?: ReactNode
  /** Still happening: a pulsing dot before the verb. */
  pending?: boolean
  /** What the chevron reveals. Absent means no chevron. */
  body?: ReactNode
  /** Controlled disclosure, for a fill whose openness is decided elsewhere
   *  (an edit that opens while it is the front of the thread). */
  open?: boolean
  defaultOpen?: boolean
  onToggle?: (open: boolean) => void
  /** The body is always shown and there is no chevron: a list of rows that IS
   *  the card, not something folded under it. */
  always?: boolean
}) {
  const [openState, setOpenState] = useState(defaultOpen)
  const open = always || (openProp ?? openState)
  const toggle = () => {
    const next = !open
    setOpenState(next)
    onToggle?.(next)
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-muted/20">
      <div className="flex items-start gap-2 px-2.5 py-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            {pending ? (
              <StatusDot
                status="working"
                title="running"
                className="mb-[1px] self-center"
              />
            ) : null}
            <span className="shrink-0 font-medium text-[0.78rem] text-foreground/70">
              {verb}
            </span>
            {mark ? <span className="shrink-0 self-center">{mark}</span> : null}
            {onOpen ? (
              <button
                type="button"
                onClick={onOpen}
                className="min-w-0 flex-1 truncate text-left font-medium text-[0.82rem] text-foreground hover:underline"
                title={label}
              >
                {label}
              </button>
            ) : (
              <span
                className="min-w-0 flex-1 truncate font-medium text-[0.82rem] text-foreground/80"
                title={label}
              >
                {label}
              </span>
            )}
            {times > 1 ? (
              <span className="shrink-0 text-[0.75rem] text-muted-foreground tabular-nums">
                ×{times}
              </span>
            ) : null}
            {trailing ? <span className="shrink-0">{trailing}</span> : null}
          </div>
          {detail ? (
            <div
              className="truncate text-[0.72rem] text-muted-foreground"
              title={detail}
            >
              {detail}
            </div>
          ) : null}
        </div>
        {body && !always ? (
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            title={open ? 'Hide' : 'Show'}
            className="mt-[1px] shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        ) : null}
      </div>
      {open && body ? (
        <div className="border-border border-t px-1 py-0.5">{body}</div>
      ) : null}
    </div>
  )
}
